import { forwardRef, useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useI18n } from "@/contexts/I18nContext";
import { useGeoLocation } from "@/contexts/LocationContext";
import { LANGUAGE_LABELS, type Language } from "@/lib/i18n";
import { useAvailableLanguages } from "@/hooks/useAvailableLanguages";
import {
  Compass,
  LogIn,
  LayoutDashboard,
  Globe,
  Sun,
  Moon,
  LogOut,
  ExternalLink,
  Shield,
  MapPin,
  Palette,
  Calculator,
  Users,
  BookOpen,
  HelpCircle,
  TrendingUp,
  Sparkles,
  Building2,
  FileText,
  type LucideIcon,
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useArtistProfile } from "@/hooks/useArtistData";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { useUserRole } from "@/hooks/useUserRole";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import SmartSearch from "@/components/SmartSearch";
import CurrencySwitcher from "@/components/CurrencySwitcher";
import { MenuToggleIcon } from "@/components/ui/menu-toggle-icon";
import { cn } from "@/lib/utils";

const LANGUAGE_FLAGS: Record<Language, string> = {
  en: "🇬🇧", nl: "🇳🇱", de: "🇩🇪", fr: "🇫🇷", es: "🇪🇸", it: "🇮🇹", pt: "🇧🇷",
  sv: "🇸🇪", pl: "🇵🇱", ja: "🇯🇵", ko: "🇰🇷", ru: "🇷🇺", tr: "🇹🇷", da: "🇩🇰",
  no: "🇳🇴", fi: "🇫🇮", cs: "🇨🇿",
};

type LinkItem = {
  title: string;
  href: string;
  icon: LucideIcon;
  description?: string;
};

/** Styled nav link for the NavigationMenuContent */
function ListItem({ title, description, icon: Icon, href }: LinkItem) {
  const navigate = useNavigate();
  return (
    <NavigationMenuLink asChild>
      <button
        onClick={() => navigate(href)}
        className="flex items-start gap-3 rounded-lg p-3 hover:bg-accent/50 transition-colors w-full text-left"
      >
        <div className="h-9 w-9 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center mt-0.5">
          <Icon className="h-4 w-4 text-primary" />
        </div>
        <div>
          <p className="text-sm font-medium text-foreground">{title}</p>
          {description && (
            <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
          )}
        </div>
      </button>
    </NavigationMenuLink>
  );
}

function MobileMenuLink({ title, icon: Icon, href, onClick }: LinkItem & { onClick: () => void }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => { navigate(href); onClick(); }}
      className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg hover:bg-accent/50 text-left transition-colors"
    >
      <Icon className="h-4 w-4 text-primary" />
      <span className="text-sm text-foreground">{title}</span>
    </button>
  );
}

function MobileMenu({ open, children }: { open: boolean; children: React.ReactNode }) {
  if (!open || typeof window === "undefined") return null;
  return createPortal(
    <div className="fixed inset-0 z-[60] bg-background/95 backdrop-blur-md overflow-y-auto">
      <div className="container mx-auto px-6 pt-20 pb-8 min-h-screen flex flex-col">
        {children}
      </div>
    </div>,
    document.body,
  );
}

function useScroll(threshold: number) {
  const [scrolled, setScrolled] = useState(false);
  const onScroll = useCallback(() => setScrolled(window.scrollY > threshold), [threshold]);
  useEffect(() => {
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);
  return scrolled;
}

const GlobalHeader = forwardRef<HTMLElement>(function GlobalHeader(_props, ref) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { t, language, setLanguage } = useI18n();
  const { data: availableLangs } = useAvailableLanguages();
  const { theme, toggleTheme } = useTheme();
  const { data: profile } = useArtistProfile();
  const subscription = useSubscriptionStatus();
  const { isSuperAdmin } = useUserRole();
  const userLocation = useGeoLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const scrolled = useScroll(10);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  if (location.pathname.startsWith("/c/")) return null;

  const isDashboard = ["/dashboard", "/admin", "/admin-super-control", "/studio"].includes(location.pathname);
  const calculatorUrl = profile?.slug ? `${window.location.origin}/c/${profile.slug}` : null;

  const cityLink = userLocation.detectedCitySlug
    ? `/tattoo/${userLocation.countrySlug}/${userLocation.detectedCitySlug}`
    : `/tattoo/${userLocation.countrySlug}`;

  const handleSignOut = async () => { await signOut(); navigate("/"); };

  const findArtistLinks: LinkItem[] = [
    { title: t("nav.by_location") || "Op Locatie", href: cityLink, icon: MapPin, description: t("nav.by_location_desc") || "Vind studio's en artiesten bij jou in de buurt" },
    { title: t("nav.by_style") || "Op Stijl", href: "/wiki", icon: Palette, description: t("nav.by_style_desc") || "Zoek artiesten gespecialiseerd in jouw stijl" },
    { title: t("nav.browse_studios") || "Ontdekken", href: "/artiesten", icon: Compass, description: t("nav.browse_studios_desc") || "Bekijk alle studio's op de kaart" },
    { title: t("inkswipe.nav.title") || "InkSwipe", href: "/swipe", icon: Sparkles, description: t("inkswipe.nav.description") || "Swipe door tattoo foto's bij jou in de buurt" },
  ];

  const toolLinks: LinkItem[] = [
    { title: t("nav.price_calculator") || "Prijscalculator", href: "/demo-calculator", icon: Calculator, description: t("nav.price_calculator_desc") || "Bereken wat jouw tattoo gaat kosten" },
    { title: t("nav.style_guide") || "Stijlgids", href: "/wiki", icon: TrendingUp, description: t("nav.style_guide_desc") || "50+ tattoo stijlen uitgelegd" },
  ];

  const forArtistLinks: LinkItem[] = [
    { title: t("nav.free_profile") || "Gratis Profiel", href: "/claim", icon: Shield, description: t("nav.free_profile_desc") || "Claim je studio en ontvang direct leads" },
    { title: t("nav.artist_resources") || "Blog & Resources", href: "/blog", icon: BookOpen, description: t("nav.artist_resources_desc") || "Tips om je studio te laten groeien" },
  ];

  const companyLinks: LinkItem[] = [
    { title: "Blog", href: "/blog", icon: FileText },
    { title: "Hoe het werkt", href: "/hoe-werkt-het", icon: HelpCircle },
  ];

  return (
    <>
      <header
        ref={ref}
        className={cn(
          "sticky top-0 z-50 border-b transition-all duration-300",
          scrolled
            ? "border-border bg-background/80 backdrop-blur-xl shadow-sm"
            : "border-transparent bg-background/60 backdrop-blur-md",
        )}
      >
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-3">
          {/* Logo */}
          <div
            className="cursor-pointer flex items-center gap-2 shrink-0"
            onClick={() => navigate("/")}
          >
            <img
              src={isDashboard && profile?.logo_url ? profile.logo_url : "/logo_groot.svg"}
              alt="Inkvaluator"
              className="h-7 sm:h-9 w-auto"
            />
            {isDashboard && profile && (
              <div className="hidden sm:flex items-center gap-2 ml-1">
                <span className="text-sm text-muted-foreground">{profile.artist_name}</span>
                <Badge
                  variant={subscription.isPro ? "default" : "secondary"}
                  className="text-[10px] px-1.5 py-0 cursor-pointer"
                  onClick={(e) => { e.stopPropagation(); navigate("/upgrade"); }}
                >
                  {subscription.isStudio ? (
                    <><Building2 className="w-3 h-3 mr-0.5" /> Studio</>
                  ) : subscription.isPro ? (
                    <><Sparkles className="w-3 h-3 mr-0.5" /> {t("sub.pro")}</>
                  ) : t("sub.free")}
                </Badge>
              </div>
            )}
          </div>

          {/* Center: Desktop Navigation — public mode only */}
          {!isDashboard && (
            <div className="hidden lg:flex items-center flex-1 justify-center">
              <NavigationMenu>
                <NavigationMenuList>
                  {/* Find Artists */}
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="text-sm font-medium text-muted-foreground hover:text-foreground bg-transparent">
                      {t("nav.find_artists") || "Vind Artiesten"}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="w-[420px] p-4 space-y-1">
                        {findArtistLinks.map((item) => (
                          <ListItem key={item.href} {...item} />
                        ))}
                        <div className="mt-3 pt-3 border-t border-border">
                          <p className="text-xs text-muted-foreground px-3">
                            {t("nav.cta_discover") || "Ontdek 1000+ studio's wereldwijd"} →{" "}
                            <button onClick={() => navigate("/artiesten")} className="text-primary hover:underline font-medium">
                              {t("nav.explore_map") || "Bekijk de kaart"}
                            </button>
                          </p>
                        </div>
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  {/* Tools */}
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="text-sm font-medium text-muted-foreground hover:text-foreground bg-transparent">
                      {t("nav.tools") || "Tools"}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="w-[420px] p-4 space-y-1">
                        {toolLinks.map((item) => (
                          <ListItem key={item.href} {...item} />
                        ))}
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  {/* For Artists */}
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="text-sm font-medium text-muted-foreground hover:text-foreground bg-transparent">
                      {t("nav.for_artists") || "Voor Artiesten"}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="w-[420px] p-4">
                        <div className="space-y-1">
                          {forArtistLinks.map((item) => (
                            <ListItem key={item.href} {...item} />
                          ))}
                        </div>
                        <div className="mt-3 pt-3 border-t border-border flex items-center gap-2">
                          {companyLinks.map((item) => (
                            <button
                              key={item.href}
                              onClick={() => navigate(item.href)}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors"
                            >
                              <item.icon className="w-3.5 h-3.5" />
                              {item.title}
                            </button>
                          ))}
                        </div>
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  {/* Blog — direct link */}
                  <NavigationMenuItem>
                    <button
                      onClick={() => navigate("/blog")}
                      className="text-sm font-medium text-muted-foreground hover:text-foreground px-3 py-2 transition-colors"
                    >
                      Blog
                    </button>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            </div>
          )}

          {/* Center: SmartSearch — public, tablet only */}
          {!isDashboard && (
            <div className="hidden sm:block lg:hidden flex-1 max-w-xs">
              <SmartSearch
                placeholder={t("landing.hero_search_placeholder") || "Zoek een stad of artiest..."}
                showNearMe={false}
                size="sm"
                inline
              />
            </div>
          )}

          {/* Right: Actions */}
          <div className="flex items-center gap-1 shrink-0">
            {/* Language picker */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="language-switcher"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-primary font-medium gap-1.5 px-2"
                >
                  <span className="text-base leading-none">
                    {availableLangs?.find((l) => l.code === language)?.flag ?? LANGUAGE_FLAGS[language] ?? "🌐"}
                  </span>
                  <Globe className="w-3.5 h-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
                {(availableLangs ?? []).map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code as Language)}
                    className={language === lang.code ? "bg-primary/10 text-primary font-medium" : ""}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    <span className="flex-1">{lang.nativeName}</span>
                    {!lang.isComplete && lang.completion > 0 && (
                      <span className="ml-2 text-[10px] text-muted-foreground">{lang.completion}%</span>
                    )}
                  </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
            </DropdownMenu>

            {/* Currency picker */}
            <CurrencySwitcher />

            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="w-9 h-9 text-muted-foreground hover:text-primary"
              onClick={toggleTheme}
              aria-label={t("header.toggle_theme")}
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>

            {isDashboard ? (
              <>
                {calculatorUrl && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="hidden sm:inline-flex"
                    onClick={() => window.open(calculatorUrl, "_blank")}
                  >
                    <ExternalLink className="w-4 h-4 mr-1" /> {t("dash.view_calculator")}
                  </Button>
                )}
                {isSuperAdmin && (
                  <Button variant="ghost" size="sm" onClick={() => navigate("/admin-super-control")}>
                    <Shield className="w-4 h-4" />
                    <span className="hidden sm:inline ml-1">{t("header.admin")}</span>
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSignOut}
                  className="text-muted-foreground hover:text-primary gap-1.5"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">{t("dash.logout")}</span>
                </Button>
              </>
            ) : (
              <>
                {/* Desktop: Sign In / Dashboard */}
                {user ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/dashboard")}
                    className="hidden md:inline-flex text-muted-foreground hover:text-primary font-medium gap-1.5"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/auth")}
                    className="hidden md:inline-flex text-muted-foreground hover:text-primary font-medium gap-1.5"
                  >
                    <LogIn className="w-4 h-4" />
                    {t("landing.login")}
                  </Button>
                )}

                {/* Desktop: CTA */}
                <Button
                  size="sm"
                  onClick={() => navigate("/claim")}
                  className="hidden lg:inline-flex gap-1.5"
                >
                  <Shield className="w-3.5 h-3.5" />
                  {t("nav.join_artist") || "Claim Profiel"}
                </Button>

                {/* Mobile hamburger */}
                <button
                  onClick={() => setMobileOpen(!mobileOpen)}
                  className="md:hidden relative z-[70] w-9 h-9 flex items-center justify-center"
                  aria-expanded={mobileOpen}
                  aria-controls="mobile-menu"
                  aria-label={t("header.toggle_menu")}
                >
                  <MenuToggleIcon open={mobileOpen} className="w-6 h-6 text-foreground" />
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Mobile menu — portal based */}
      <MobileMenu open={mobileOpen}>
        <nav className="space-y-1 flex-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-2">
            {t("nav.find_artists") || "Vind Artiesten"}
          </p>
          {findArtistLinks.map((link) => (
            <MobileMenuLink key={link.href} {...link} onClick={() => setMobileOpen(false)} />
          ))}

          <div className="h-px bg-border my-4" />

          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-2">
            {t("nav.tools") || "Tools"}
          </p>
          {toolLinks.map((link) => (
            <MobileMenuLink key={link.href} {...link} onClick={() => setMobileOpen(false)} />
          ))}

          <div className="h-px bg-border my-4" />

          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-2">
            {t("nav.for_artists") || "Voor Artiesten"}
          </p>
          {forArtistLinks.map((link) => (
            <MobileMenuLink key={link.href} {...link} onClick={() => setMobileOpen(false)} />
          ))}

          <div className="h-px bg-border my-4" />

          {companyLinks.map((link) => (
            <MobileMenuLink key={link.href} {...link} onClick={() => setMobileOpen(false)} />
          ))}
        </nav>

        <div className="mt-6 space-y-3 pt-6 border-t border-border">
          {user ? (
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={() => { navigate("/dashboard"); setMobileOpen(false); }}
            >
              <LayoutDashboard className="w-4 h-4" />
              Dashboard
            </Button>
          ) : (
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={() => { navigate("/auth"); setMobileOpen(false); }}
            >
              <LogIn className="w-4 h-4" />
              {t("landing.login")}
            </Button>
          )}
          <Button
            className="w-full gap-2"
            onClick={() => { navigate("/claim"); setMobileOpen(false); }}
          >
            <Shield className="w-4 h-4" />
            {t("nav.join_artist") || "Claim Je Profiel"}
          </Button>
        </div>
      </MobileMenu>
    </>
  );
});

GlobalHeader.displayName = "GlobalHeader";

export default GlobalHeader;
